import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useT } from '@/i18n';
import { toast } from '@/stores/toast.store';
import { apiCall, ApiError } from '@/lib/api-client';
import { clientsApi, type ClientProfile } from './clients.api';

/**
 * State-aware action button for a patient profile. Renders the right CTA
 * for the viewing role + current assignment state:
 *
 *  - agent owning the active assignment → "Ir a pre-call" (with preflight
 *    refetch — if the assignment was revoked by IdleSessionReaper between
 *    the profile read and the click, surface a clear toast instead of a
 *    Bad Request on /pre-call). Cluster A followup — A-1 + A-2.
 *  - agent + foreign active assignment → "Solicitar reasignación".
 *  - agent + no active assignment → "Solicitar asignación".
 *  - admin → link to /admin/reassign (existing UI handles both assign/reassign).
 *  - supervisor / uploader → no action button.
 */
export function AssignmentAction(props: {
  profile: ClientProfile;
  currentUser: { id: string; role: string };
}): JSX.Element | null {
  const { profile, currentUser } = props;
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const aa = profile.activeAssignment;

  const requestReassign = useMutation({
    mutationFn: () => clientsApi.requestReassign(profile.id),
    onSuccess: () => {
      toast.success(
        t('clientProfile.requestReassign.successTitle'),
        t('clientProfile.requestReassign.successBody'),
      );
    },
    onError: (err) => {
      toast.error(
        t('clientProfile.requestReassign.errorTitle'),
        (err as Error).message,
      );
    },
  });

  const goPreCall = async (): Promise<void> => {
    if (!aa) return;
    try {
      // Preflight: refresh the active claim from the backend before navigating.
      // If the assignment was revoked (IdleSessionReaper, released by another
      // tab, etc) the endpoint returns 404. We catch and surface a clear toast.
      await apiCall(`/agents/me/queue/${encodeURIComponent(aa.id)}/details`);
      navigate(`/pre-call/${aa.id}`);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 410)) {
        toast.error(
          t('clientProfile.goToPreCall.staleTitle'),
          t('clientProfile.goToPreCall.staleBody'),
        );
        queryClient.invalidateQueries({ queryKey: ['clients.profile', profile.id] });
        return;
      }
      toast.error(t('clientProfile.goToPreCall.errorTitle'), (err as Error).message);
    }
  };

  // Agent: own active assignment → "go to pre-call" (with preflight)
  if (currentUser.role === 'agent' && aa && aa.agentId === currentUser.id) {
    return (
      <Button size="sm" onClick={goPreCall}>
        {t('clientProfile.goToPreCall')}
      </Button>
    );
  }

  // Agent: foreign active assignment → "request reassign"
  if (currentUser.role === 'agent' && aa && aa.agentId !== currentUser.id) {
    return (
      <Button
        size="sm"
        variant="secondary"
        onClick={() => requestReassign.mutate()}
        disabled={requestReassign.isPending}
      >
        {t('clientProfile.requestReassign.action')}
      </Button>
    );
  }

  // Agent: no active assignment → "request assignment"
  if (currentUser.role === 'agent' && !aa) {
    return (
      <Button
        size="sm"
        variant="secondary"
        onClick={() => requestReassign.mutate()}
        disabled={requestReassign.isPending}
      >
        {t('clientProfile.requestAssignment.action')}
      </Button>
    );
  }

  // Admin: link to existing reassignment page (no new UI)
  if (currentUser.role === 'admin') {
    return (
      <Button asChild size="sm" variant="secondary">
        <Link to="/admin/reassign">
          {aa ? t('clientProfile.adminReassign') : t('clientProfile.adminAssign')}
        </Link>
      </Button>
    );
  }

  // Supervisor / uploader / unknown role: no action
  return null;
}
